import React, { useState, useEffect } from 'react';
import api from '../api';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const pageList = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'customers', label: 'Customers' },
  { key: 'loans', label: 'Loans' },
  { key: 'collections', label: 'Collections' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'income', label: 'Income' },
  { key: 'users_page', label: 'Users' },
  { key: 'my_balance', label: 'My Balance' },
];

export default function Users() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser && currentUser.role === 'admin';
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [allWallets, setAllWallets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [downloadingLive, setDownloadingLive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('collector');
  const [tab, setTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [editPerms, setEditPerms] = useState({});
  const [showAdminTransfer, setShowAdminTransfer] = useState(null);
  const [adminTransferAmount, setAdminTransferAmount] = useState('');
  const [showAdjust, setShowAdjust] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [showWalletHistory, setShowWalletHistory] = useState(null);
  const [walletHistory, setWalletHistory] = useState([]);
  const [walletHistoryLoading, setWalletHistoryLoading] = useState(false);
  const [adjustReason, setAdjustReason] = useState('');

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (tab === 'backups' && isAdmin) loadBackups(); }, [tab]);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get('/auth/users'),
      api.get('/auth/collection-requests'),
      api.get('/auth/wallets'),
      api.get('/accounts')
    ]).then(([uRes, rRes, wRes, aRes]) => {
      setUsers(uRes.data);
      setRequests(rRes.data);
      setAllWallets(wRes.data);
      setAccounts(aRes.data);
      setLoading(false);
    }).catch(err => { console.error(err); setLoading(false); });
  };

  const createUser = (e) => {
    e.preventDefault();
    if (!username || !fullName) return toast.error('Required');
    api.post('/auth/users', { username, full_name: fullName, password: password || 'password123', role }).then(() => {
      toast.success('User created!');
      setShowForm(false); setUsername(''); setFullName(''); setPassword(''); loadData();
    }).catch(err => toast.error(err.response?.data?.error || 'Failed'));
  };

  const openEdit = (u) => {
    setShowEdit(u);
    setUsername(u.username);
    setFullName(u.full_name || '');
    setPassword('');
    setRole(u.role);
    setEditPerms({
      dashboard: u.dashboard || 0, customers: u.customers || 0, loans: u.loans || 0,
      collections: u.collections || 0, accounts: u.accounts || 0, expenses: u.expenses || 0,
      income: u.income || 0, users_page: u.users_page || 0, my_balance: u.my_balance || 0,
    });
  };

  const saveEdit = (e) => {
    e.preventDefault();
    const body = { username, full_name: fullName, role, permissions: editPerms };
    if (password) body.password = password;
    api.put('/auth/users/' + showEdit.id, body).then(() => {
      toast.success('User updated!');
      setShowEdit(null); loadData();
    }).catch(err => toast.error(err.response?.data?.error || 'Failed'));
  };

  const togglePerm = (key) => {
    setEditPerms(prev => ({ ...prev, [key]: prev[key] ? 0 : 1 }));
  };

  const handleRequest = (reqId, status) => {
    api.put('/auth/collection-request/' + reqId, { status, approved_by: currentUser?.id }).then((res) => {
      toast.success(res.data.message);
      loadData();
    }).catch(err => toast.error(err.response?.data?.error || 'Failed'));
  };

  const adminTransfer = (e) => {
    e.preventDefault();
    const wallet = showAdminTransfer;
    const matchingAccount = accounts.find(a => a.type === wallet.type);
    if (!matchingAccount) return toast.error('No ' + wallet.type + ' account');
    if (parseFloat(adminTransferAmount) > wallet.balance) return toast.error('Insufficient');
    api.post('/auth/collection-request', {
      user_id: wallet.user_id, amount: parseFloat(adminTransferAmount),
      account_id: matchingAccount.id, notes: 'Admin transfer'
    }).then((res) => {
      api.put('/auth/collection-request/' + res.data.id, { status: 'approved', approved_by: currentUser?.id }).then(() => {
        toast.success('Transfer complete!');
        setShowAdminTransfer(null); setAdminTransferAmount(''); loadData();
      });
    }).catch(err => toast.error(err.response?.data?.error || 'Failed'));
  };

  const adjustWallet = (e) => {
    e.preventDefault();
    const wallet = showAdjust;
    const amt = parseFloat(adjustAmount);
    if (!amt) return toast.error('Enter a non-zero amount (negative to reduce, e.g. 1000 to bring a -1000 balance to 0)');
    if (!adjustReason.trim()) return toast.error('A reason is required');
    api.post('/auth/wallets/' + wallet.user_id + '/' + wallet.type + '/adjust', { amount: amt, reason: adjustReason }).then((res) => {
      toast.success('Balance adjusted: ' + fmt(res.data.old_balance) + ' \u2192 ' + fmt(res.data.new_balance));
      setShowAdjust(null); setAdjustAmount(''); setAdjustReason(''); loadData();
    }).catch(err => toast.error(err.response?.data?.error || 'Failed'));
  };

  // Wallet statement -- at your request: a wallet only ever showed a lump
  // balance with no way to see which payments actually make it up.
  const viewWalletHistory = (w) => {
    setShowWalletHistory(w);
    setWalletHistory([]);
    setWalletHistoryLoading(true);
    api.get('/auth/wallets/' + w.user_id + '/' + w.type + '/transactions').then(res => {
      setWalletHistory(res.data);
      setWalletHistoryLoading(false);
    }).catch(err => { toast.error(err.response?.data?.error || 'Failed to load wallet history'); setWalletHistoryLoading(false); });
  };

  const fmt = (n) => '₹' + (n || 0).toLocaleString('en-IN');

  // Downloads go through axios (not a plain window.open/fetch) so the
  // existing auth interceptor in api.js attaches the Authorization header
  // automatically -- a raw window.open can never carry a custom header,
  // which was the exact bug fixed earlier in ExportButton.js.
  const triggerDownload = (blob, filename) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const downloadLiveBackup = () => {
    setDownloadingLive(true);
    api.get('/backup/download', { responseType: 'blob' }).then(res => {
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="(.+)"/);
      triggerDownload(res.data, match ? match[1] : 'nirav-finance-backup.sql');
    }).catch(() => toast.error('Backup failed')).finally(() => setDownloadingLive(false));
  };

  const loadBackups = () => {
    setBackupsLoading(true);
    api.get('/backup/list').then(res => setBackups(res.data)).catch(() => {}).finally(() => setBackupsLoading(false));
  };

  const downloadSavedBackup = (filename) => {
    api.get('/backup/download/' + encodeURIComponent(filename), { responseType: 'blob' })
      .then(res => triggerDownload(res.data, filename))
      .catch(() => toast.error('Download failed'));
  };

  const fmtBytes = (n) => {
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
    return (n / 1024 / 1024).toFixed(1) + ' MB';
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading...</div>;

  return (
    <div>
      <Toaster position="top-right" />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 'bold' }}>Users & Wallets</h2>
        <button onClick={() => setShowForm(true)} style={{ background: '#2563eb', color: 'white', padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer' }}>+ Add User</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['users', 'wallets', 'requests'].concat(isAdmin ? ['backups'] : []).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 500, background: tab === t ? '#2563eb' : 'white', color: tab === t ? 'white' : '#4b5563', textTransform: 'capitalize' }}>
            {t} {t === 'requests' ? '(' + requests.length + ')' : ''}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>ID</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Username</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Full Name</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Role</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Actions</th>
            </tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '10px 14px' }}>{u.id}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 500 }}>{u.username}</td>
                  <td style={{ padding: '10px 14px' }}>{u.full_name || '-'}</td>
                  <td style={{ padding: '10px 14px' }}><span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, background: u.role === 'admin' ? '#dbeafe' : '#fef3c7', color: u.role === 'admin' ? '#1e40af' : '#92400e', textTransform: 'capitalize' }}>{u.role}</span></td>
                  <td style={{ padding: '10px 14px' }}>
                    <button onClick={() => openEdit(u)} style={{ background: '#2563eb', color: 'white', border: 'none', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'wallets' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {allWallets.map(w => (
            <div key={w.id} style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderTop: '4px solid ' + (w.type === 'cash' ? '#16a34a' : '#2563eb') }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div><h3 style={{ fontSize: 16, fontWeight: 600, textTransform: 'capitalize', margin: 0 }}>{w.type} Wallet</h3><p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0 0' }}>{w.full_name || w.username}</p></div>
                <span style={{ fontSize: 28 }}>{w.type === 'cash' ? '💵' : '🏦'}</span>
              </div>
              <p style={{ fontSize: 28, fontWeight: 'bold', color: w.balance > 0 ? '#16a34a' : (w.balance < 0 ? '#dc2626' : '#6b7280'), margin: '12px 0' }}>{fmt(w.balance)}</p>
              <div style={{ display: 'flex', gap: 6 }}>
                {/* Shows exactly which payments/adjustments make up this
                    balance, at your request -- a wallet used to be just a
                    number with no way to see what's actually in it. */}
                <button onClick={() => viewWalletHistory(w)} style={{ padding: '8px 12px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>View</button>
                <button onClick={() => { setShowAdminTransfer(w); setAdminTransferAmount(''); }} disabled={w.balance <= 0} style={{ flex: 1, padding: '8px 0', background: w.balance > 0 ? '#7c3aed' : '#d1d5db', color: 'white', border: 'none', borderRadius: 6, cursor: w.balance > 0 ? 'pointer' : 'default', fontSize: 13, fontWeight: 500 }}>Transfer to Company</button>
                {/* Manual correction — needed e.g. after deleting a mistaken
                    payment leaves a wallet negative (see routes/payments.js) */}
                <button onClick={() => { setShowAdjust(w); setAdjustAmount(''); setAdjustReason(''); }} style={{ padding: '8px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Adjust</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'requests' && (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>User</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Amount</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Notes</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Actions</th>
            </tr></thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '10px 16px', fontWeight: 500 }}>{r.full_name} (@{r.username})</td>
                  <td style={{ padding: '10px 16px', fontWeight: 600 }}>{fmt(r.amount)}</td>
                  <td style={{ padding: '10px 16px' }}>{r.notes || '-'}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <button onClick={() => handleRequest(r.id, 'approved')} style={{ background: '#16a34a', color: 'white', border: 'none', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 8 }}>Approve</button>
                    <button onClick={() => handleRequest(r.id, 'rejected')} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Reject</button>
                  </td>
                </tr>
              ))}
              {requests.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>No pending requests</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'backups' && isAdmin && (
        <div>
          <div style={{ background: 'white', borderRadius: 8, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Download Live Backup</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Generates a fresh, complete database backup right now (schema + every row) as a single .sql file. Restoring it recreates the entire database from scratch.</p>
            <button onClick={downloadLiveBackup} disabled={downloadingLive} style={{ padding: '10px 20px', background: downloadingLive ? '#93c5fd' : '#2563eb', color: 'white', border: 'none', borderRadius: 8, cursor: downloadingLive ? 'default' : 'pointer', fontWeight: 500 }}>
              {downloadingLive ? 'Generating...' : '⬇ Download Live Backup'}
            </button>
          </div>

          <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
            <div style={{ padding: '16px 20px 4px 20px' }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Automatic Backups</h3>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0 0' }}>A backup runs automatically once a day and is kept here for 14 days.</p>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead><tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Created</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Size</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Actions</th>
              </tr></thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.filename} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '10px 16px' }}>{new Date(b.created_at).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '10px 16px', color: '#6b7280' }}>{fmtBytes(b.size)}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <button onClick={() => downloadSavedBackup(b.filename)} style={{ background: '#4b5563', color: 'white', border: 'none', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Download</button>
                    </td>
                  </tr>
                ))}
                {!backupsLoading && backups.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>No automatic backups yet — the first one runs shortly after the server starts.</td></tr>}
                {backupsLoading && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>Loading...</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Add User</h3>
            <form onSubmit={createUser}>
              <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Username *</label><input type="text" value={username} onChange={e => setUsername(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
              <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Full Name *</label><input type="text" value={fullName} onChange={e => setFullName(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
              <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Password</label><input type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="password123" style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
              <div style={{ marginBottom: 16 }}><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Role</label><select value={role} onChange={e => setRole(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }}><option value="collector">Collector</option><option value="admin">Admin</option></select></div>
              <div style={{ display: 'flex', gap: 8 }}><button type="submit" style={{ flex: 1, padding: 10, background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Create</button><button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: 10, background: '#e5e7eb', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button></div>
            </form>
          </div>
        </div>
      )}

      {showEdit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Edit User: {showEdit.username}</h3>
            <form onSubmit={saveEdit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div><label style={{ fontSize: 13, fontWeight: 500 }}>Username *</label><input type="text" value={username} onChange={e => setUsername(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 500 }}>Full Name *</label><input type="text" value={fullName} onChange={e => setFullName(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 500 }}>New Password</label><input type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="Leave blank to keep" style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 500 }}>Role</label><select value={role} onChange={e => setRole(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }}><option value="collector">Collector</option><option value="admin">Admin</option></select></div>
              </div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>Page Access</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                {pageList.map(p => (
                  <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '4px 0' }}>
                    <input type="checkbox" checked={!!editPerms[p.key]} onChange={() => togglePerm(p.key)} style={{ width: 16, height: 16 }} />
                    {p.label}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" style={{ flex: 1, padding: 10, background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Save Changes</button>
                <button type="button" onClick={() => setShowEdit(null)} style={{ flex: 1, padding: 10, background: '#e5e7eb', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAdminTransfer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Admin Transfer</h3>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>{showAdminTransfer.full_name || showAdminTransfer.username} - {showAdminTransfer.type} Wallet: {fmt(showAdminTransfer.balance)}</p>
            <form onSubmit={adminTransfer}>
              <div style={{ marginBottom: 16 }}><label>Amount *</label><input type="number" value={adminTransferAmount} onChange={e => setAdminTransferAmount(e.target.value)} required min="1" max={showAdminTransfer.balance} style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', fontSize: 16 }} autoFocus /></div>
              <div style={{ display: 'flex', gap: 8 }}><button type="submit" style={{ flex: 1, padding: 10, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Transfer Now</button><button type="button" onClick={() => setShowAdminTransfer(null)} style={{ flex: 1, padding: 10, background: '#e5e7eb', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button></div>
            </form>
          </div>
        </div>
      )}

      {showAdjust && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Adjust Balance</h3>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>{showAdjust.full_name || showAdjust.username} - {showAdjust.type} Wallet: current {fmt(showAdjust.balance)}</p>
            <form onSubmit={adjustWallet}>
              <div style={{ marginBottom: 12 }}>
                <label>Amount to add *</label>
                <input type="number" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} required style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', fontSize: 16 }} autoFocus placeholder="e.g. 1000 to bring -1,000 to 0" />
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Positive adds to the balance, negative subtracts. New balance will be {fmt(showAdjust.balance + (parseFloat(adjustAmount) || 0))}.</div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>Reason *</label>
                <input type="text" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} required placeholder="e.g. Correcting after deleted payment #1953" style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}><button type="submit" style={{ flex: 1, padding: 10, background: '#4b5563', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Apply Adjustment</button><button type="button" onClick={() => setShowAdjust(null)} style={{ flex: 1, padding: 10, background: '#e5e7eb', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button></div>
            </form>
          </div>
        </div>
      )}

      {showWalletHistory && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 4 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{showWalletHistory.full_name || showWalletHistory.username} — {showWalletHistory.type} Wallet</h3>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0 0' }}>Current balance: <strong>{fmt(showWalletHistory.balance)}</strong></p>
              </div>
              <button onClick={() => setShowWalletHistory(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflow: 'auto', marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Date</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Customer</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Description</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: '#6b7280' }}>Amount</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: '#6b7280' }}>Balance After</th>
                </tr></thead>
                <tbody>
                  {walletHistoryLoading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>Loading...</td></tr>}
                  {!walletHistoryLoading && walletHistory.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No transactions yet</td></tr>}
                  {!walletHistoryLoading && walletHistory.map(t => (
                    <tr key={t.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{new Date(t.created_at).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 12px' }}>{t.customer_name ? (t.customer_name + (t.client_number ? ' (#' + t.client_number + ')' : '')) : ''}</td>
                      <td style={{ padding: '8px 12px', color: '#374151' }}>{t.description}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: t.type === 'credit' ? '#16a34a' : '#dc2626' }}>{t.type === 'credit' ? '+' : '-'}{fmt(t.amount)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280' }}>{fmt(t.running_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
